<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import {
  AddTreeGraphNode,
  CheckType,
  CreateCheckConfigurationCommand,
  CreateImageStepUpload,
  CreateSnapshotUpload,
  CreateStep,
  CreateVideoStepUpload,
  GraphEntityType,
  PermissionOrganization,
  StepProposalConverterImageMutation,
  StepProposalConverterImageMutationVariables,
  StepProposalConverterSnapshotMutation,
  StepProposalConverterSnapshotMutationVariables,
  StepProposalConverterStepMutation,
  StepProposalConverterStepMutationVariables,
  StepProposalResultDetailsFragment,
  VideoExtension,
} from '@/generated/graphql'
import ViewVideoStepUpload from '@/components/stepUpload/ViewVideoStepUpload.vue'
import { computed, reactive, ref, watch } from 'vue'
import StepTypePicker from '@/components/input/StepTypePicker.vue'
import { useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { v4 } from 'uuid'
import useSave from '@/composables/useSave'
import useVuelidate from '@vuelidate/core'
import { createStepValidationRules } from '@/validation/step'
import { normalDescription, required } from '@/validation'
import NotificationSaved from '@/components/notifications/NotificationSaved.vue'
import useAuthorization from '@/composables/useAuthorization'
import AsyncDialog from '@/components/dialogs/AsyncDialog.vue'
import TextField from '@/components/input/TextField.vue'
import TextAreaField from '@/components/input/TextAreaField.vue'
import CheckTypePicker from '@/components/input/CheckTypePicker.vue'
import MilliQuantityField from '@/components/input/MilliQuantityField.vue'
import Checkbox from '@/components/input/Checkbox.vue'
import { caption as captionValidation } from '@/components/stepUpload/stepUploadValidation'

const checkTypeIcon = (type: CheckType): string => {
  if (type === CheckType.String) return 'article'
  if (type === CheckType.Numeric) return 'pin'
  if (type === CheckType.Confirmation) return 'task_alt'
  if (type === CheckType.FileUpload) return 'upload_file'
  throw new Error(`Unexpected check type: ${type}`)
}

export type StepProposalConverterStepProposal = StepProposalResultDetailsFragment['proposals'][0]
type LocalUploadProposal = StepProposalConverterStepProposal['uploadProposals'][0]
type LocalVideoStepUpload = Pick<
  LocalUploadProposal,
  'fragmentStartInMillis' | 'fragmentEndInMillis'
> & { fragmentLoop: boolean; caption: undefined }

const props = defineProps<{
  proposal: StepProposalConverterStepProposal
  productId: string
  moduleId: string
  bopId: string
}>()
const emit = defineEmits<{
  created: []
  discard: []
}>()

const { t } = useI18n()

const { reactiveOrgAuthz } = useAuthorization()
const canSnapshot = reactiveOrgAuthz(
  PermissionOrganization.OrgProductWrite,
  PermissionOrganization.OrgUploadWrite,
)

const isVideoProposal = (proposal: LocalUploadProposal): boolean => {
  const ext = proposal.upload.extension as VideoExtension | undefined
  return (ext as VideoExtension | undefined)?.__typename === 'VideoExtension'
}

const asVideoExtension = (proposal: LocalUploadProposal): VideoExtension => {
  return proposal.upload.extension as VideoExtension
}

const tabsModel = ref()
const selectedUploadProposalIds = ref<string[]>([])

type SnapshotUpload = { id: string; url: string; caption?: string }
const snapshotUploads = ref<SnapshotUpload[]>([])
const selectedSnapshotIds = ref<string[]>([])

watch(
  () => props.proposal,
  (v) => {
    tabsModel.value = v.uploadProposals[0]?.id
    selectedUploadProposalIds.value = v.uploadProposals.map((u) => u.id)
  },
  { immediate: true },
)

watch(
  () => props.proposal.id,
  () => {
    snapshotUploads.value = []
    selectedSnapshotIds.value = []
  },
)

const videoCurrentTimesMap = reactive<Record<string, number>>({})

const snapshotCaptionModel = reactive<{ caption: string }>({ caption: '' })
const snapshotCaptionValidation = useVuelidate<{ caption: string }>(
  { caption: captionValidation },
  snapshotCaptionModel,
)
const snapshotDialog = ref<InstanceType<typeof AsyncDialog>>()
const pendingSnapshotProposalId = ref<string | null>(null)

type CreateModel = Partial<CreateStep>
const createModel = reactive<CreateModel>({})
const validation = useVuelidate<CreateModel>(createStepValidationRules, createModel)
watch(
  () => props.proposal,
  (v) => {
    createModel.id = v4()
    createModel.toolIds = []
    createModel.productId = props.productId
    createModel.name = v.name
    createModel.durationInSeconds = v.durationInSeconds
    createModel.instructionText = v.instructionText
    validation.value.$reset()
  },
  { immediate: true },
)

const stepMutation = useMutation<
  StepProposalConverterStepMutation,
  StepProposalConverterStepMutationVariables
>(gql`
  mutation StepProposalConverterStep($command: CreateStep!, $node: AddTreeGraphNode!) {
    product {
      createStep(command: $command) {
        id
      }
      addNodeToModuleGraph(command: $node)
    }
  }
`)
const videoMutation = useMutation(gql`
  mutation StepProposalConverterVideos($commands: [CreateVideoStepUpload!]!) {
    product {
      createVideoStepUploads(commands: $commands) {
        id
      }
    }
  }
`)
const imageMutation = useMutation<
  StepProposalConverterImageMutation,
  StepProposalConverterImageMutationVariables
>(gql`
  mutation StepProposalConverterImage($command: CreateImageStepUpload!) {
    product {
      createImageStepUpload(command: $command) {
        id
      }
    }
  }
`)
const snapshotMutation = useMutation<
  StepProposalConverterSnapshotMutation,
  StepProposalConverterSnapshotMutationVariables
>(gql`
  mutation StepProposalConverterSnapshot($command: CreateSnapshotUpload!) {
    upload {
      createSnapshotUpload(command: $command) {
        id
        url
      }
    }
  }
`)
const checkMutation = useMutation(gql`
  mutation StepProposalConverterCheck($command: CreateCheckConfigurationCommand!) {
    product {
      createCheckConfiguration(command: $command) {
        id
      }
    }
  }
`)

const removedCheckProposalIds = ref<Set<string>>(new Set())
type CheckProposalEdit = {
  id: string
  type: CheckType
  description: string
  upperLimit?: number
  lowerLimit?: number
  isRequired: boolean
}
const checkProposalEdits = ref<Map<string, CheckProposalEdit>>(new Map())
watch(
  () => props.proposal,
  () => {
    removedCheckProposalIds.value = new Set()
    checkProposalEdits.value = new Map()
  },
  { immediate: true },
)
const visibleCheckProposals = computed(() =>
  (props.proposal.checkProposals ?? [])
    .filter((c) => !removedCheckProposalIds.value.has(c.id))
    .map((c) => checkProposalEdits.value.get(c.id) ?? c),
)
const removeCheckProposal = (id: string) => {
  removedCheckProposalIds.value = new Set([...removedCheckProposalIds.value, id])
}

const editCheckModel = reactive<Partial<CheckProposalEdit>>({})
const editCheckValidation = useVuelidate<Partial<CheckProposalEdit>>(
  {
    type: { required },
    description: { required, normalDescription },
  },
  editCheckModel,
)
watch(
  () => editCheckModel.type,
  (v) => {
    if (v != CheckType.Numeric) {
      editCheckModel.upperLimit = undefined
      editCheckModel.lowerLimit = undefined
    }
  },
)
const editCheckDialog = ref<InstanceType<typeof AsyncDialog>>()
const editCheckProposal = async (check: (typeof visibleCheckProposals.value)[0]) => {
  Object.assign(editCheckModel, {
    id: check.id,
    type: check.type,
    description: check.description,
    upperLimit: check.upperLimit ?? undefined,
    lowerLimit: check.lowerLimit ?? undefined,
    isRequired: check.isRequired,
  })
  editCheckValidation.value.$reset()
  await editCheckDialog.value?.open()
}
const onConfirmEditCheck = async () => {
  checkProposalEdits.value = new Map([
    ...checkProposalEdits.value,
    [editCheckModel.id!, { ...editCheckModel } as CheckProposalEdit],
  ])
}

const createVideoCommands = computed<CreateVideoStepUpload[]>(() =>
  selectedUploadProposalIds.value
    .map((id) => props.proposal.uploadProposals.find((u) => u.id == id))
    .filter((p): p is LocalUploadProposal => !!p && isVideoProposal(p))
    .map(
      (p): CreateVideoStepUpload => ({
        id: v4(),
        stepId: createModel.id as string,
        uploadId: p.upload.id,
        fragmentStartInMillis: p.fragmentStartInMillis,
        fragmentEndInMillis: p.fragmentEndInMillis,
        fragmentLoop: false,
        caption: undefined,
      }),
    ),
)

const onSnapshot = async () => {
  const activeId = tabsModel.value
  const activeProposal = props.proposal.uploadProposals.find((u) => u.id == activeId)
  if (!activeProposal || !isVideoProposal(activeProposal)) return

  pendingSnapshotProposalId.value = activeId
  snapshotCaptionModel.caption = ''
  await snapshotDialog.value?.open()
}

const onConfirmSnapshot = async () => {
  const proposalId = pendingSnapshotProposalId.value
  const activeProposal = props.proposal.uploadProposals.find((u) => u.id == proposalId)
  if (!activeProposal) return

  const command: CreateSnapshotUpload = {
    uploadId: activeProposal.upload.id,
    timestampInMillis: Math.round(videoCurrentTimesMap[proposalId!] || 0),
  }
  const result = await snapshotMutation.mutate({ command })
  const upload = result?.data?.upload?.createSnapshotUpload
  if (upload) {
    snapshotUploads.value.push({
      id: upload.id,
      url: upload.url,
      caption: snapshotCaptionModel.caption || undefined,
    })
    selectedSnapshotIds.value.push(upload.id)
    tabsModel.value = upload.id
  }
}

const onCreated = async () => {
  const id = createModel.id as string
  const node: AddTreeGraphNode = {
    entityId: props.moduleId,
    node: {
      entityType: GraphEntityType.Step,
      entityId: id,
      parentId: undefined,
      id: v4(),
    },
  }
  await stepMutation.mutate({ command: createModel as CreateStep, node })

  if (createVideoCommands.value.length > 0) {
    await videoMutation.mutate({ commands: createVideoCommands.value })
  }

  for (const proposalId of selectedUploadProposalIds.value) {
    const proposal = props.proposal.uploadProposals.find((u) => u.id == proposalId)
    if (proposal && !isVideoProposal(proposal)) {
      const command: CreateImageStepUpload = { id: v4(), stepId: id, uploadId: proposal.upload.id }
      await imageMutation.mutate({ command })
    }
  }

  for (const snapshot of snapshotUploads.value.filter((u) =>
    selectedSnapshotIds.value.includes(u.id),
  )) {
    const command: CreateImageStepUpload = {
      id: v4(),
      stepId: id,
      uploadId: snapshot.id,
      caption: snapshot.caption,
    }
    await imageMutation.mutate({ command })
  }

  for (const [index, check] of visibleCheckProposals.value.entries()) {
    const command: CreateCheckConfigurationCommand = {
      bopId: props.bopId,
      moduleId: props.moduleId,
      stepId: id,
      type: check.type,
      description: check.description,
      upperLimit: check.upperLimit ?? undefined,
      lowerLimit: check.lowerLimit ?? undefined,
      order: index,
      isRequired: check.isRequired,
    }
    await checkMutation.mutate({ command })
  }

  emit('created')
}
const { saveButtonProps, showSavedNotification } = useSave(onCreated, validation)
</script>

<template>
  <v-card class="mb-3">
    <v-card-text>
      <v-row dense>
        <v-col cols="6">
          <h2>{{ props.proposal.name }}</h2>

          <step-type-picker
            v-model="createModel.stepTypeId"
            :label="t('entity.step.field.stepType')"
            :validation="validation.stepTypeId"
          />

          <text-area-field
            v-model="createModel.instructionText"
            :label="t('entity.step.field.instructionText')"
            class="mt-3"
          />

          <div v-if="visibleCheckProposals.length > 0" class="mt-3">
            <h3>{{ t('entity.checkConfiguration.plural') }}</h3>
            <v-list density="compact" class="pa-0">
              <v-list-item
                v-for="check in visibleCheckProposals"
                :key="check.id"
                class="check-proposal-item"
              >
                <template #prepend>
                  <v-icon :icon="checkTypeIcon(check.type)" class="check-proposal-icon" />
                </template>
                <v-list-item-title>
                  {{ check.description }}
                </v-list-item-title>
                <v-list-item-subtitle
                  v-if="
                    check.type === 'NUMERIC' && check.lowerLimit != null && check.upperLimit != null
                  "
                >
                  {{ check.lowerLimit / 1000 }} - {{ check.upperLimit / 1000 }}
                </v-list-item-subtitle>
                <template #append>
                  <v-btn
                    icon="edit"
                    size="small"
                    variant="text"
                    @click="editCheckProposal(check)"
                  />
                  <v-btn
                    icon="delete"
                    size="small"
                    variant="text"
                    @click="removeCheckProposal(check.id)"
                  />
                </template>
              </v-list-item>
            </v-list>
          </div>
        </v-col>
        <v-col cols="6">
          <v-tabs
            v-if="props.proposal.uploadProposals.length > 0 || snapshotUploads.length > 0"
            v-model="tabsModel"
          >
            <v-tab v-for="(u, i) in props.proposal.uploadProposals" :key="u.id" :value="u.id">
              <v-checkbox
                v-model="selectedUploadProposalIds"
                :value="u.id"
                hide-details
                @click.stop
              />
              {{ i + 1 }}
            </v-tab>
            <v-tab v-for="(u, i) in snapshotUploads" :key="u.id" :value="u.id">
              <v-checkbox v-model="selectedSnapshotIds" :value="u.id" hide-details @click.stop />
              S{{ i + 1 }}
            </v-tab>
          </v-tabs>

          <v-tabs-window v-model="tabsModel">
            <v-tabs-window-item
              v-for="u in props.proposal.uploadProposals"
              :key="u.id"
              :value="u.id"
              class="pt-2"
            >
              <view-video-step-upload
                v-if="isVideoProposal(u)"
                :src="asVideoExtension(u).url"
                :options="
                  {
                    fragmentStartInMillis: u.fragmentStartInMillis,
                    fragmentEndInMillis: u.fragmentEndInMillis,
                    fragmentLoop: false,
                    caption: undefined,
                  } as LocalVideoStepUpload
                "
                :show-snapshot="true"
                :snapshot-authorized="canSnapshot"
                width="100%"
                height="100%"
                @current-time-in-millis="(millis) => (videoCurrentTimesMap[u.id] = millis)"
                @snapshot="onSnapshot"
              />
              <img v-else :src="u.upload.url" style="max-width: 100%" />
            </v-tabs-window-item>

            <v-tabs-window-item v-for="u in snapshotUploads" :key="u.id" :value="u.id" class="pt-2">
              <img :src="u.url" style="max-width: 100%" />
            </v-tabs-window-item>
          </v-tabs-window>
        </v-col>
      </v-row>

      <notification-saved :model-value="showSavedNotification" />

      <async-dialog ref="snapshotDialog" :validation="snapshotCaptionValidation" max-width="500">
        <template #title>
          {{ t('entity.stepUpload.snapshot.dialogTitle') }}
        </template>
        <text-field
          v-model="snapshotCaptionModel.caption"
          :validation="snapshotCaptionValidation.caption"
          :label="t('entity.stepUpload.snapshot.caption')"
        />
        <template #actions="{ resolve, loading }">
          <v-btn variant="text" @click="resolve()">
            {{ t('button.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            :loading="loading"
            @click="resolve(onConfirmSnapshot)"
          >
            {{ t('button.save') }}
          </v-btn>
        </template>
      </async-dialog>

      <async-dialog ref="editCheckDialog" max-width="650">
        <template #title>
          {{ t('entity.checkConfiguration.suggestions') }}
        </template>
        <check-type-picker
          v-model="editCheckModel.type"
          required
          :validation="editCheckValidation.type"
          :label="t('entity.checkConfiguration.field.type')"
        />
        <text-field
          v-model="editCheckModel.description"
          required
          :validation="editCheckValidation.description"
          :label="t('entity.checkConfiguration.field.description')"
        />
        <checkbox
          v-model="editCheckModel.isRequired"
          :label="t('entity.checkConfiguration.field.isRequired')"
        />
        <milli-quantity-field
          v-if="editCheckModel.type == CheckType.Numeric"
          v-model="editCheckModel.lowerLimit"
          :label="t('entity.checkConfiguration.field.lowerLimit')"
        />
        <milli-quantity-field
          v-if="editCheckModel.type == CheckType.Numeric"
          v-model="editCheckModel.upperLimit"
          :label="t('entity.checkConfiguration.field.upperLimit')"
        />
        <template #actions="{ resolve, loading }">
          <v-btn variant="text" @click="resolve()">
            {{ t('button.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            :loading="loading"
            @click="
              () => {
                editCheckValidation.$touch()
                if (!editCheckValidation.$invalid) resolve(onConfirmEditCheck)
              }
            "
          >
            {{ t('button.save') }}
          </v-btn>
        </template>
      </async-dialog>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn @click="emit('discard')">
        {{ t('button.discard') }}
      </v-btn>
      <v-btn variant="elevated" color="primary" v-bind="saveButtonProps">
        {{ t('button.create') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<style scoped lang="scss">
.check-proposal-item {
  padding-inline-start: 0 !important;

  :deep(.v-list-item__prepend) {
    .v-list-item__spacer {
      width: 12px !important;
    }
  }

  .v-list-item-title {
    white-space: normal !important;
  }
}
</style>
